import { useMemo, useState } from "react";
import { Save, Plus, Globe } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Btn } from "../../components/Btn";
import { MeshDiagram } from "../../components/MeshDiagram";
import { useToast } from "../../components/Toast";
import { useApiFallback } from "../../hooks/useApiFallback";
import { getLocalNet } from "../../api/endpoints";
import type { LocalPeerLink } from "../../api/types";
import { localServers, NODE_INFO, type LocalServer } from "../../data";

// Fallback peer-links — выводим из mock localServers (все, кроме самого узла),
// чтобы UI оставался информативным без бэкенда.
const FALLBACK_PEERS: LocalPeerLink[] = localServers
  .filter((s) => s.id !== NODE_INFO.id)
  .map((s, i) => ({
    peer_id: s.id,
    peer_location: s.location,
    status: s.status === "online" ? "active" : s.status === "stale" ? "degraded" : "down",
    latency_ms: 18 + i * 6,
    last_sync_at: null,
    timeseries: [],
  }));

// Маппинг peer-link → LocalServer, чтобы скормить MeshDiagram (он же ожидает
// LocalServer-форму). status mesh-канала «active/degraded/down» → online/stale/offline.
function toLocalServerFromPeer(p: LocalPeerLink): LocalServer {
  const status: LocalServer["status"] =
    p.status === "active" ? "online" : p.status === "degraded" ? "stale" : "offline";
  return {
    id: p.peer_id,
    location: p.peer_location,
    address: "",
    status,
    clients: 0,
    cacheGB: 0,
    cacheCapGB: 0,
    lastSyncMin: 0,
    generation: 0,
    certExpires: "",
  };
}

function loadSavedNet(): { bindIp: string; port: string; domain: string; ca: string } {
  const defaults = {
    bindIp: "10.10.10.11",
    port: "8443",
    domain: "msk-01.market.corp",
    ca: "Корпоративный УЦ Заказчика",
  };
  try {
    return { ...defaults, ...JSON.parse(localStorage.getItem("astramarket.local.net") ?? "{}") };
  } catch {
    return defaults;
  }
}

export default function NetPage() {
  const push = useToast();
  const savedNet = useMemo(loadSavedNet, []);
  const [bindIp, setBindIp] = useState(savedNet.bindIp);
  const [port, setPort] = useState(savedNet.port);
  const [domain, setDomain] = useState(savedNet.domain);
  const [ca, setCa] = useState(savedNet.ca);
  const [manualPeers, setManualPeers] = useState<LocalPeerLink[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("astramarket.local.manualPeers") ?? "[]") as LocalPeerLink[];
    } catch {
      return [];
    }
  });
  const [latencyOverrides, setLatencyOverrides] = useState<Record<string, number>>(() => {
    try {
      return JSON.parse(localStorage.getItem("astramarket.local.peerLatency") ?? "{}") as Record<string, number>;
    } catch {
      return {};
    }
  });

  const netQ = useQuery({
    queryKey: ["local", "net"],
    queryFn: getLocalNet,
    refetchInterval: 20_000,
  });
  const peers = useApiFallback<LocalPeerLink[]>(netQ.data, netQ.isError, FALLBACK_PEERS);
  const displayPeers = useMemo(
    () => [...peers.value, ...manualPeers].map((peer) => ({
      ...peer,
      latency_ms: latencyOverrides[peer.peer_id] ?? peer.latency_ms,
    })),
    [peers.value, manualPeers, latencyOverrides],
  );

  // Меш собирается из peer-связей + сам локал (NODE_INFO).
  const meshLocals = useMemo<LocalServer[]>(() => {
    const self: LocalServer = {
      id: NODE_INFO.id,
      location: NODE_INFO.location,
      address: "",
      status: "online",
      clients: NODE_INFO.clients,
      cacheGB: NODE_INFO.diskGB,
      cacheCapGB: NODE_INFO.diskCapGB,
      lastSyncMin: NODE_INFO.syncMinutesAgo,
      generation: NODE_INFO.generation,
      certExpires: NODE_INFO.certExpires,
    };
    return [self, ...displayPeers.map(toLocalServerFromPeer)];
  }, [displayPeers]);

  function saveSettings() {
    localStorage.setItem("astramarket.local.net", JSON.stringify({ bindIp, port, domain, ca }));
    push({ kind: "success", text: "Сетевые параметры сохранены" });
  }

  function addPeer() {
    const peerId = window.prompt("ID соседнего локала, например spb-02")?.trim();
    if (!peerId) return;
    if (displayPeers.some((peer) => peer.peer_id === peerId)) {
      push({ kind: "warn", text: `Сосед ${peerId} уже добавлен` });
      return;
    }
    const location = window.prompt("Название локации")?.trim() || peerId;
    const next: LocalPeerLink[] = [...manualPeers, {
      peer_id: peerId,
      peer_location: location,
      status: "down",
      latency_ms: 0,
      last_sync_at: null,
      timeseries: [],
    }];
    setManualPeers(next);
    localStorage.setItem("astramarket.local.manualPeers", JSON.stringify(next));
    push({ kind: "success", text: `Сосед ${peerId} добавлен` });
  }

  function configurePeer(peer: LocalPeerLink) {
    const raw = window.prompt(`Задержка для ${peer.peer_id}, мс`, String(peer.latency_ms));
    if (raw === null) return;
    const latency = Number(raw);
    if (!Number.isFinite(latency) || latency < 0) {
      push({ kind: "warn", text: "Введите неотрицательное число" });
      return;
    }
    const next = { ...latencyOverrides, [peer.peer_id]: latency };
    setLatencyOverrides(next);
    localStorage.setItem("astramarket.local.peerLatency", JSON.stringify(next));
    push({ kind: "success", text: `Параметры ${peer.peer_id} сохранены` });
  }

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Сеть узла
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            Параметры сетевого слушателя, TLS, соседних локалов
            {netQ.isFetching && " · обновляется…"}
          </div>
        </div>
        <Btn icon={Save} variant="primary" onClick={saveSettings}>
          Сохранить
        </Btn>
      </header>

      <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
          Меш-связи узла
        </h2>
        <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
          Видимые соседи и каналы. Активных peer-каналов: {displayPeers.filter((p) => p.status === "active").length}.
        </p>
        <div className="mt-3">
          <MeshDiagram height={300} locals={meshLocals} />
        </div>
      </section>

      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">Слушатель</h2>
          <div className="mt-4 space-y-3.5">
            <Field label="IP-адрес">
              <input value={bindIp} onChange={(e) => setBindIp(e.target.value)} className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-[13px] outline-none focus:border-[var(--color-fg-muted)]" />
            </Field>
            <Field label="Порт mTLS">
              <input value={port} onChange={(e) => setPort(e.target.value)} className="w-32 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-[13px] outline-none focus:border-[var(--color-fg-muted)]" />
            </Field>
            <Field label="Доменное имя">
              <input value={domain} onChange={(e) => setDomain(e.target.value)} className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-[13px] outline-none focus:border-[var(--color-fg-muted)]" />
            </Field>
          </div>
        </section>

        <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">TLS и сертификаты</h2>
          <div className="mt-4 space-y-3.5">
            <Field label="Удостоверяющий центр">
              <input value={ca} onChange={(e) => setCa(e.target.value)} className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] outline-none focus:border-[var(--color-fg-muted)]" />
            </Field>
            <Field label="Клиентский сертификат" hint="Используется для mTLS с Глобалом и соседями.">
              <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3 text-[12px]">
                <div className="flex items-center gap-2 text-[var(--color-fg)]">
                  <Globe size={14} className="text-[var(--color-success)]" />
                  <span className="font-mono">CN={NODE_INFO.id}</span>
                </div>
                <div className="mt-1 font-mono text-[11px] text-[var(--color-fg-muted)]">
                  Действует до {NODE_INFO.certExpires} · выпущен корпоративным УЦ
                </div>
              </div>
            </Field>
            <Field label="Минимальная версия TLS">
              <div className="flex gap-2">
                <Pill>TLS 1.3</Pill>
                <Pill muted>TLS 1.2 (fallback)</Pill>
              </div>
            </Field>
          </div>
        </section>
      </div>

      <section className="mt-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">Известные соседи</h2>
            <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
              С этими локалами текущий узел может обмениваться в обход Глобала
            </p>
          </div>
          <Btn icon={Plus} size="sm" onClick={addPeer}>Добавить</Btn>
        </div>
        <div className="-mx-1 mt-3 overflow-x-auto">
          <table className="w-full min-w-[640px] text-[12.5px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
                <Th>ID</Th><Th>Локация</Th><Th>Состояние</Th><Th>Latency</Th><Th>Посл. синк</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {displayPeers.map((p, i) => (
                <tr key={p.peer_id} style={{ animationDelay: `${i * 20}ms` }} className="stagger-up border-t border-[var(--color-border)]">
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono">{p.peer_id}</td>
                  <td className="whitespace-nowrap px-3 py-2.5">{p.peer_location}</td>
                  <td className="whitespace-nowrap px-3 py-2.5">
                    <span
                      className="mr-1 inline-block h-1.5 w-1.5 rounded-full align-middle"
                      style={{
                        background:
                          p.status === "active"
                            ? "var(--color-success)"
                            : p.status === "degraded"
                            ? "var(--color-warning)"
                            : "var(--color-danger)",
                      }}
                    />
                    {p.status === "active" ? "канал активен" : p.status === "degraded" ? "ухудшен" : "недоступен"}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono">{p.latency_ms} мс</td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[var(--color-fg-muted)]">
                    {p.last_sync_at ? new Date(p.last_sync_at).toLocaleString("ru-RU") : "—"}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-right">
                    <button
                      onClick={() => configurePeer(p)}
                      className="text-[12px] text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]"
                    >настроить</button>
                  </td>
                </tr>
              ))}
              {!displayPeers.length && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-[12.5px] text-[var(--color-fg-muted)]">
                  Соседних локалов пока нет.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[12px] font-medium text-[var(--color-fg)]">{label}</div>
      {hint && <div className="mb-1 text-[11px] text-[var(--color-fg-muted)]">{hint}</div>}
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Pill({ children, muted = false }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <span className={
      "rounded-lg border px-2 py-1 font-mono text-[11.5px] " +
      (muted
        ? "border-[var(--color-border)] text-[var(--color-fg-muted)]"
        : "border-[var(--color-brand)] bg-[var(--color-brand)] text-white")
    }>
      {children}
    </span>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}
